import React, { Fragment, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  styled,
} from '@mui/material';
import { hubsupportalApi } from 'app/utils/utils';

const StyledFormControl = styled(FormControl)(() => ({
  minWidth: 120,
  marginBottom: 16,
}));



const OfficeDialog = ({
  settingsOpen,
  handleSettingsClose,
  environment,
  setEnvironment,
  selectedPartner,
  setSelectedPartner,
  selectedOrganization,
  setSelectedOrganization,
  selectedOffice,
  setSelectedOffice,
}) => {

    const [officeOptions, setOfficeOptions] = useState([]);
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [organizationOptions, setOrganizationOptions] = useState(['Org 1', 'Org 2', 'Org 3']); // TODO: Replace with real data
  
    const [officeLoading, setOfficeLoading] = useState(false);
    const [partnerLoading, setPartnerLoading] = useState(false);
    const [organizationLoading, setOrganizationLoading] = useState(false);
  
    const loadPartners = (fts) => {
      setPartnerLoading(true);
  
      let url = "find_core_partner?env=" + environment;
      if (fts && fts.length > 2)
        url = url + "&fts=" + fts;
      hubsupportalApi.get(url, { cache_timeout: 3600 })
        .then(response => {
          if (response.data.status === "success") {
            const partners = response.data.data.list || [];
            const partnerOptions = partners
              .map(partner => ({
                name: partner.name || '',
                uid: partner.uid || ''
              }))
              .filter(partner => partner.name && partner.uid);
            setPartnerOptions(partnerOptions);
          }
        })
        .catch(error => {
          console.error("Error fetching partners:", error);
        })
        .finally(() => {
          setPartnerLoading(false);
        });
  
      setPartnerLoading(false);
  
    };
  
    const loadOrganizations = (fts) => {
      setOrganizationLoading(true);
  
      if (selectedPartner == null) {
        setOrganizationOptions([]);
      } else {
        let url = "find_core_organization?env=" + environment + "&partner_uid=" + selectedPartner?.uid;
        if (fts && fts.length > 2)
          url = url + "&fts=" + fts;
        hubsupportalApi.get(url, { cache_timeout: 3600 })
          .then(response => {
            if (response.data.status === "success") {
              const organizations = response.data.data.list || [];
              const organizationOptions = organizations
                .map(organization => ({
                  name: organization.name || '',
                  uid: organization.uid || ''
                }))
                .filter(organization => organization.name && organization.uid);
              setOrganizationOptions(organizationOptions);
            }
          })
          .catch(error => {
            console.error("Error fetching organizations:", error);
          })
          .finally(() => {
            setOrganizationLoading(false);
          });
      }
  
      setOrganizationLoading(false);
  
    };
  
    const loadOffices = (fts) => {
      setOfficeLoading(true);
  
      if (selectedPartner == null) {
        setOfficeOptions([]);
      } else {
        let url = "find_core_office?env=" + environment + "&organization_uid=" + selectedOrganization?.uid;
        if (fts && fts.length > 2)
          url = url + "&fts=" + fts;
        hubsupportalApi.get(url, { cache_timeout: 3600 })
          .then(response => {
            if (response.data.status === "success") {
              const offices = response.data.data.list || [];
              const officeOptions = offices
                .map(office => ({
                  name: office.name || '',
                  uid: office.uid || ''
                }))
                .filter(office => office.name && office.uid);
              setOfficeOptions(officeOptions);
            }
          })
          .catch(error => {
            console.error("Error fetching offices:", error);
          })
          .finally(() => {
            setOfficeLoading(false);
          });
      }
  
      setOfficeLoading(false);
  
    };

    useEffect(() => {
        loadPartners(selectedPartner)
      }, [environment]);
    
    
  useEffect(() => {
    setOrganizationOptions([]);
    setSelectedOrganization(null);
    loadOrganizations(selectedOrganization)
  }, [selectedPartner]);

  useEffect(() => {
    setOfficeOptions([]);
    setSelectedOffice(null);
    loadOffices(selectedOrganization)
  }, [selectedOrganization]);

  return (
    <Dialog open={settingsOpen} onClose={handleSettingsClose}>
      <DialogTitle>Patient Search Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
          <StyledFormControl>
            <label style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.6)' }}>Environment</label>
            <Select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              displayEmpty
              inputProps={{ 'aria-label': 'Environment' }}
            >
              <MenuItem value="dev">dev</MenuItem>
              <MenuItem value="qa">qa</MenuItem>
              <MenuItem value="prod">prod</MenuItem>
            </Select>
          </StyledFormControl>

          <Box>
            <label style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}>Partner</label>
            <Autocomplete
              value={selectedPartner}
              onChange={(event, newValue) => {
                setSelectedPartner(newValue);
                console.log('Selected partner:', newValue);
              }}
              options={partnerOptions}
              loading={partnerLoading}
              freeSolo
              getOptionLabel={(option) => option?.name || ''}
              onInputChange={(event, newValue) => {
                loadPartners(newValue);
              }}
              renderOption={(props, option) => <li {...props}>{option.name}</li>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select a partner"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {partnerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>

          <Box>
            <label style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}>Organization</label>
            <Autocomplete
              value={selectedOrganization}
              onChange={(event, newValue) => {
                setSelectedOrganization(newValue);
                console.log('Selected Organization:', newValue);
              }}
              options={organizationOptions}
              loading={organizationLoading}
              freeSolo
              getOptionLabel={(option) => option?.name || ''}
              onInputChange={(event, newValue) => {
                loadOrganizations(newValue);
              }}
              renderOption={(props, option) => <li {...props}>{option.name}</li>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select an organization"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {organizationLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>

          <Box>
            <label style={{ marginBottom: '8px', color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}>Office</label>
            <Autocomplete
              value={selectedOffice}
              onChange={(event, newValue) => {
                setSelectedOffice(newValue);
                console.log('Selected Office:', newValue);
              }}
              options={officeOptions}
              loading={officeLoading}
              freeSolo
              getOptionLabel={(option) => option?.name || ''}
              onInputChange={(event, newValue) => {
                loadOffices(newValue);
              }}
              renderOption={(props, option) => <li {...props}>{option.name}</li>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search or select an office"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {officeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSettingsClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfficeDialog;